/**
 * Performance Profiling Utilities
 *
 * Task 4.1.6: Tools for measuring React render performance and physics impact.
 * Provides utilities for tracking FPS, frame times, component render counts,
 * and generating performance reports.
 *
 * Usage:
 * - Use PerformanceProfiler to track metrics over time
 * - Enable with ?profile=true URL parameter
 * - View results in console or export to JSON
 *
 * @example
 * const profiler = new PerformanceProfiler()
 * profiler.startProfiling()
 * // ... perform operations ...
 * const report = profiler.generateReport()
 * console.log(report)
 */

export interface FrameMetrics {
  timestamp: number
  fps: number
  frameTime: number // in ms
  brickCount: number
  physicsEnabled: boolean
}

export interface ComponentRenderMetrics {
  componentName: string
  renderCount: number
  totalRenderTime: number // in ms
  averageRenderTime: number // in ms
}

export interface PerformanceReport {
  testDuration: number // in ms
  totalFrames: number
  averageFPS: number
  minFPS: number
  maxFPS: number
  averageFrameTime: number // in ms
  maxFrameTime: number // in ms
  brickCount: number
  physicsEnabled: boolean
  percentile95FrameTime: number // in ms
  percentile99FrameTime: number // in ms
  frameMetrics: FrameMetrics[]
  componentMetrics: Map<string, ComponentRenderMetrics>
}

/**
 * Check if performance profiling is enabled.
 * Profiling is enabled by adding `?profile=true` to the URL.
 *
 * @returns {boolean} True if profiling should be enabled
 */
export function isProfilingEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  const params = new URLSearchParams(window.location.search)
  return params.get('profile') === 'true'
}

/**
 * PerformanceProfiler Class
 *
 * Tracks performance metrics over time and generates detailed reports.
 * Used for Task 4.1.6 to quantify physics impact and identify bottlenecks.
 */
export class PerformanceProfiler {
  private frameMetrics: FrameMetrics[] = []
  private componentRenderCounts: Map<string, number> = new Map()
  private componentRenderTimes: Map<string, number> = new Map()
  private startTime: number = 0
  private profilingActive: boolean = false
  private lastFrameTime: number = 0
  private physicsEnabled: boolean = true

  /**
   * Start profiling session
   */
  startProfiling(physicsEnabled: boolean = true): void {
    this.profilingActive = true
    this.startTime = performance.now()
    this.lastFrameTime = this.startTime
    this.frameMetrics = []
    this.componentRenderCounts.clear()
    this.componentRenderTimes.clear()
    this.physicsEnabled = physicsEnabled

    console.log(
      `[PerformanceProfiler] Started profiling (physics: ${physicsEnabled ? 'enabled' : 'disabled'})`
    )
  }

  /**
   * Stop profiling session
   */
  stopProfiling(): void {
    this.profilingActive = false
    console.log('[PerformanceProfiler] Stopped profiling')
  }

  /**
   * Record a frame's performance metrics
   * Should be called every frame from useFrame hook
   */
  recordFrame(fps: number, brickCount: number): void {
    if (!this.profilingActive) return

    const now = performance.now()
    const frameTime = now - this.lastFrameTime
    this.lastFrameTime = now

    this.frameMetrics.push({
      timestamp: now - this.startTime,
      fps,
      frameTime,
      brickCount,
      physicsEnabled: this.physicsEnabled,
    })
  }

  /**
   * Record a component render
   * Should be called from component useEffect or render method
   */
  recordComponentRender(componentName: string, renderTime: number = 0): void {
    if (!this.profilingActive) return

    const currentCount = this.componentRenderCounts.get(componentName) || 0
    const currentTime = this.componentRenderTimes.get(componentName) || 0

    this.componentRenderCounts.set(componentName, currentCount + 1)
    this.componentRenderTimes.set(componentName, currentTime + renderTime)
  }

  /**
   * Generate performance report from collected metrics
   */
  generateReport(): PerformanceReport {
    const totalFrames = this.frameMetrics.length

    if (totalFrames === 0) {
      console.warn('[PerformanceProfiler] No frame data collected')
      return {
        testDuration: 0,
        totalFrames: 0,
        averageFPS: 0,
        minFPS: 0,
        maxFPS: 0,
        averageFrameTime: 0,
        maxFrameTime: 0,
        brickCount: 0,
        physicsEnabled: this.physicsEnabled,
        percentile95FrameTime: 0,
        percentile99FrameTime: 0,
        frameMetrics: [],
        componentMetrics: new Map(),
      }
    }

    // Calculate FPS stats
    const fpsValues = this.frameMetrics.map((m) => m.fps)
    const frameTimeValues = this.frameMetrics.map((m) => m.frameTime)

    const averageFPS = fpsValues.reduce((a, b) => a + b, 0) / totalFrames
    const minFPS = Math.min(...fpsValues)
    const maxFPS = Math.max(...fpsValues)

    const averageFrameTime =
      frameTimeValues.reduce((a, b) => a + b, 0) / totalFrames
    const maxFrameTime = Math.max(...frameTimeValues)

    // Calculate percentiles
    const sortedFrameTimes = [...frameTimeValues].sort((a, b) => a - b)
    const percentile95Index = Math.floor(totalFrames * 0.95)
    const percentile99Index = Math.floor(totalFrames * 0.99)
    const percentile95FrameTime = sortedFrameTimes[percentile95Index] || 0
    const percentile99FrameTime = sortedFrameTimes[percentile99Index] || 0

    // Get brick count (from last frame)
    const brickCount =
      this.frameMetrics[this.frameMetrics.length - 1]?.brickCount || 0

    // Calculate component metrics
    const componentMetrics = new Map<string, ComponentRenderMetrics>()
    this.componentRenderCounts.forEach((renderCount, componentName) => {
      const totalRenderTime =
        this.componentRenderTimes.get(componentName) || 0
      componentMetrics.set(componentName, {
        componentName,
        renderCount,
        totalRenderTime,
        averageRenderTime: totalRenderTime / renderCount,
      })
    })

    const testDuration = performance.now() - this.startTime

    return {
      testDuration,
      totalFrames,
      averageFPS,
      minFPS,
      maxFPS,
      averageFrameTime,
      maxFrameTime,
      brickCount,
      physicsEnabled: this.physicsEnabled,
      percentile95FrameTime,
      percentile99FrameTime,
      frameMetrics: this.frameMetrics,
      componentMetrics,
    }
  }

  /**
   * Export report as JSON string
   */
  exportReportJSON(report: PerformanceReport): string {
    // Convert Map to object for JSON serialization
    const componentMetricsObj: Record<string, ComponentRenderMetrics> = {}
    report.componentMetrics.forEach((value, key) => {
      componentMetricsObj[key] = value
    })

    const exportData = {
      ...report,
      componentMetrics: componentMetricsObj,
      // Don't export full frame metrics array (too large)
      frameMetrics: undefined,
      frameMetricsSample: report.frameMetrics.slice(0, 100), // Just first 100 frames
    }

    return JSON.stringify(exportData, null, 2)
  }

  /**
   * Print report to console
   */
  printReport(report: PerformanceReport): void {
    console.group('[PerformanceProfiler] Performance Report')
    console.log(`Test Duration: ${(report.testDuration / 1000).toFixed(2)}s`)
    console.log(`Total Frames: ${report.totalFrames}`)
    console.log(`Brick Count: ${report.brickCount}`)
    console.log(
      `Physics: ${report.physicsEnabled ? 'ENABLED' : 'DISABLED'}`
    )
    console.log('')
    console.log('FPS Metrics:')
    console.log(`  Average: ${report.averageFPS.toFixed(2)} FPS`)
    console.log(`  Min: ${report.minFPS.toFixed(2)} FPS`)
    console.log(`  Max: ${report.maxFPS.toFixed(2)} FPS`)
    console.log('')
    console.log('Frame Time Metrics:')
    console.log(`  Average: ${report.averageFrameTime.toFixed(2)} ms`)
    console.log(`  Max: ${report.maxFrameTime.toFixed(2)} ms`)
    console.log(`  95th Percentile: ${report.percentile95FrameTime.toFixed(2)} ms`)
    console.log(`  99th Percentile: ${report.percentile99FrameTime.toFixed(2)} ms`)
    console.log('')
    console.log('Component Render Counts:')

    // Sort components by render count
    const sortedComponents = Array.from(report.componentMetrics.values())
      .sort((a, b) => b.renderCount - a.renderCount)
      .slice(0, 10) // Top 10

    sortedComponents.forEach((metric) => {
      console.log(
        `  ${metric.componentName}: ${metric.renderCount} renders`
      )
    })
    console.groupEnd()
  }

  /**
   * Get current profiling status
   */
  isProfilingActive(): boolean {
    return this.profilingActive
  }
}

/**
 * Global profiler instance for easy access
 */
export const globalProfiler = new PerformanceProfiler()

/**
 * React hook for component render tracking
 *
 * Usage in component:
 * ```tsx
 * useRenderTracking('MyComponent')
 * ```
 */
export function useRenderTracking(componentName: string): void {
  if (!isProfilingEnabled()) return

  const renderStart = performance.now()

  // Track render in useEffect (runs after render)
  React.useEffect(() => {
    const renderTime = performance.now() - renderStart
    globalProfiler.recordComponentRender(componentName, renderTime)
  })
}

// Export React for the hook
import React from 'react'
